<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Plus } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import AgentPickerModal from '../components/AgentPickerModal.vue'
import ColorPickerModal from '../components/ColorPickerModal.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import {
  createSimpleProductionOrder,
  getBomVersion,
  getProductionOrder,
  listAgentScopeOptions,
  listColorReferences,
  listProductBomVersions,
  listProductionOrders,
  listTechnicalPassports,
} from '../productService'
import type {
  AgentScopeOption,
  BomVersion,
  ColorReference,
  EngineeringProduct,
  ProductionOrderVariantPayload,
} from '../types'

type AgentTarget = 'department' | 'source' | 'destination'

const client = useApiClient()
const router = useRouter()
const { t } = useI18n()
const productPickerOpen = ref(false)
const agentPickerOpen = ref(false)
const agentTarget = ref<AgentTarget>('department')
const colorPickerOpen = ref(false)
const colorPickerIndex = ref(-1)
const departments = ref<AgentScopeOption[]>([])
const warehouses = ref<AgentScopeOption[]>([])
const colors = ref<ColorReference[]>([])
const selectedProduct = ref<EngineeringProduct | null>(null)
const activeBom = ref<BomVersion | null>(null)
const bomLoading = ref(false)
const saving = ref(false)
const loadError = ref('')
const submitError = ref('')
const fieldErrors = reactive<Record<string, string>>({})

function randomCode(length = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('')
}

function generateMoCode(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `MO-${pad(now.getDate())}${pad(now.getMonth() + 1)}${String(now.getFullYear()).slice(-2)}-${randomCode()}`
}

const form = reactive({
  mo_code: generateMoCode(),
  department_agent_id: 0,
  source_warehouse_agent_id: 0,
  destination_warehouse_agent_id: 0,
  variants: [{ color_name: '', size_label: '', planned_quantity: 0 }] as ProductionOrderVariantPayload[],
})

const suggestedSizeRanges = [
  '16-18',
  '19-21',
  '22-24',
  '25-27',
  '28-30',
  '31-33',
  '34-36',
  '36-38',
  '39-41',
  '42-44',
  '45-47',
] as const

/**
 * Size suggestions: sizes the product was produced in before (past order
 * variants and technical passport size ranges) plus the standard ranges. The
 * field stays free text, so a brand-new size can always be typed.
 */
const sizeOptions = ref<string[]>([...suggestedSizeRanges])
const sizeHistoryLoading = ref(false)
const SIZE_HISTORY_ORDER_LIMIT = 8

function sortSizes(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' })
}

async function loadSizeHistory(productId: number): Promise<void> {
  sizeHistoryLoading.value = true
  const sizes = new Set<string>()
  try {
    const [passports, orders] = await Promise.all([
      listTechnicalPassports(client, productId).catch(() => []),
      listProductionOrders(client, { product_id: productId, page: 1, per_page: SIZE_HISTORY_ORDER_LIMIT }).catch(() => null),
    ])
    for (const passport of passports) {
      for (const piece of String(passport.size_range ?? '').split(/[,;/]+/)) {
        const size = piece.trim()
        if (size) sizes.add(size)
      }
    }
    const orderIds = (orders?.items ?? []).slice(0, SIZE_HISTORY_ORDER_LIMIT).map((order) => order.id)
    const details = await Promise.allSettled(orderIds.map((orderId) => getProductionOrder(client, orderId)))
    for (const detail of details) {
      if (detail.status !== 'fulfilled') continue
      for (const variant of detail.value.variants ?? []) {
        const size = String(variant.size_label ?? '').trim()
        if (size) sizes.add(size)
      }
    }
  } finally {
    // The standard ranges stay as a fallback for a product with no history yet.
    sizeOptions.value = [...new Set([...sizes, ...suggestedSizeRanges])].sort(sortSizes)
    sizeHistoryLoading.value = false
  }
}

const totalQuantity = computed(() => form.variants.reduce(
  (total, row) => total + (Number(row.planned_quantity) || 0),
  0,
))
const selectedDepartment = computed(() => departments.value.find((item) => item.id === form.department_agent_id))
const selectedSource = computed(() => warehouses.value.find((item) => item.id === form.source_warehouse_agent_id))
const selectedDestination = computed(() => warehouses.value.find((item) => item.id === form.destination_warehouse_agent_id))
const pickerItems = computed(() => agentTarget.value === 'department' ? departments.value : warehouses.value)
const pickerTitle = computed(() => agentTarget.value === 'department'
  ? t('engineering.productionOrder.selectDepartment')
  : agentTarget.value === 'source'
    ? t('engineering.productionOrder.selectSourceWarehouse')
    : t('engineering.productionOrder.selectDestinationWarehouse'))

function addVariant(): void {
  form.variants.push({ color_name: '', size_label: '', planned_quantity: 0 })
}

function removeVariant(index: number): void {
  if (form.variants.length === 1) return
  form.variants.splice(index, 1)
}

function openColorPicker(index: number): void {
  colorPickerIndex.value = index
  colorPickerOpen.value = true
}

function selectColor(color: ColorReference): void {
  // A colour created from the picker is not in the initially loaded catalog yet.
  if (!colors.value.some((item) => item.id === color.id)) {
    colors.value = [...colors.value, color]
  }
  const row = form.variants[colorPickerIndex.value]
  if (row) {
    row.color_reference_id = color.id
    row.color_name = color.name
  }
  colorPickerOpen.value = false
}

/** Colour name with its catalog code: `Black (BLK)`. */
function colorLabel(row: ProductionOrderVariantPayload): string {
  if (!row.color_name) return t('engineering.simpleOrder.selectColor')
  const color = colors.value.find((item) => item.id === row.color_reference_id)
  return color?.code ? `${row.color_name} (${color.code})` : row.color_name
}

function openAgentPicker(target: AgentTarget): void {
  agentTarget.value = target
  agentPickerOpen.value = true
}

function selectAgent(agent: AgentScopeOption): void {
  if (agentTarget.value === 'department') form.department_agent_id = agent.id
  if (agentTarget.value === 'source') form.source_warehouse_agent_id = agent.id
  if (agentTarget.value === 'destination') form.destination_warehouse_agent_id = agent.id
  agentPickerOpen.value = false
}

async function selectProduct(product: EngineeringProduct): Promise<void> {
  selectedProduct.value = product
  productPickerOpen.value = false
  activeBom.value = null
  bomLoading.value = true
  submitError.value = ''
  void loadSizeHistory(product.id)
  try {
    const versions = await listProductBomVersions(client, product.id)
    const active = versions.find((version) => Boolean(version.is_active))
    if (active) activeBom.value = await getBomVersion(client, active.id)
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  } finally {
    bomLoading.value = false
  }
}

function validate(): boolean {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key])
  if (!selectedProduct.value) fieldErrors.product = t('engineering.simpleOrder.productRequired')
  if (!activeBom.value?.operations.length) fieldErrors.bom = t('engineering.simpleOrder.bomRequired')
  if (!form.department_agent_id) fieldErrors.department = t('engineering.productionOrder.departmentRequired')
  if (!form.source_warehouse_agent_id) fieldErrors.source = t('engineering.simpleOrder.sourceRequired')
  if (!form.destination_warehouse_agent_id) fieldErrors.destination = t('engineering.simpleOrder.destinationRequired')
  if (form.source_warehouse_agent_id === form.destination_warehouse_agent_id && form.source_warehouse_agent_id) {
    fieldErrors.destination = t('engineering.productionOrder.warehousesMustDiffer')
  }
  const seen = new Set<string>()
  form.variants.forEach((row, index) => {
    if (!row.color_name.trim() || !row.size_label.trim() || Number(row.planned_quantity) <= 0) {
      fieldErrors[`variant_${index}`] = t('engineering.simpleOrder.variantRequired')
    }
    const key = `${row.color_name.trim().toLocaleLowerCase()}|${row.size_label.trim().toLocaleLowerCase()}`
    if (seen.has(key)) fieldErrors[`variant_${index}`] = t('engineering.simpleOrder.duplicateVariant')
    seen.add(key)
  })
  if (totalQuantity.value <= 0) fieldErrors.total = t('engineering.simpleOrder.totalRequired')
  return Object.keys(fieldErrors).length === 0
}

async function submit(): Promise<void> {
  if (!validate() || !selectedProduct.value) return
  saving.value = true
  submitError.value = ''
  try {
    const order = await createSimpleProductionOrder(client, {
      mo_code: form.mo_code,
      product_id: selectedProduct.value.id,
      department_agent_id: form.department_agent_id,
      source_warehouse_agent_id: form.source_warehouse_agent_id,
      destination_warehouse_agent_id: form.destination_warehouse_agent_id,
      planned_quantity: totalQuantity.value,
      variants: form.variants.map((row) => ({
        color_reference_id: row.color_reference_id,
        color_name: row.color_name.trim(),
        size_label: row.size_label.trim(),
        planned_quantity: Number(row.planned_quantity),
      })),
    })
    await router.push(`/production-orders/${order.id}`)
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const [departmentRows, warehouseRows, colorRows] = await Promise.all([
      listAgentScopeOptions(client, 'department'),
      listAgentScopeOptions(client, 'warehouse'),
      listColorReferences(client),
    ])
    departments.value = departmentRows
    warehouses.value = warehouseRows
    colors.value = colorRows
  } catch (error: unknown) {
    loadError.value = formatApiError(error)
  }
})
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" to="/production-orders">{{ t('engineering.simpleOrder.back') }}</RouterLink>
  </div>
  <form class="simple-order" @submit.prevent="submit">
    <header>
      <div><p class="eyebrow">{{ t('engineering.simpleOrder.eyebrow') }}</p><h2>{{ t('engineering.simpleOrder.title') }}</h2><p>{{ t('engineering.simpleOrder.help') }}</p></div>
      <button type="submit" :disabled="saving">{{ saving ? t('engineering.simpleOrder.activating') : t('engineering.simpleOrder.activate') }}</button>
    </header>
    <p v-if="loadError" class="form-error">{{ loadError }}</p>
    <p v-if="submitError" class="form-error">{{ submitError }}</p>

    <div class="order-layout">
      <div class="order-column">
        <section>
          <h3>{{ t('engineering.simpleOrder.setup') }}</h3>
      <div class="form-grid">
        <label>{{ t('engineering.productionOrder.moCode') }}<input v-model="form.mo_code" readonly></label>
        <label>{{ t('engineering.simpleOrder.product') }} *
          <button class="picker-button" type="button" @click="productPickerOpen = true">{{ selectedProduct?.name || t('engineering.simpleOrder.selectProduct') }}</button>
          <small v-if="fieldErrors.product">{{ fieldErrors.product }}</small>
        </label>
        <label>{{ t('engineering.productionOrder.department') }} *<button class="picker-button" type="button" @click="openAgentPicker('department')">{{ selectedDepartment?.name || t('engineering.productionOrder.selectDepartment') }}</button><small v-if="fieldErrors.department">{{ fieldErrors.department }}</small></label>
        <label>{{ t('engineering.productionOrder.sourceWarehouse') }} *<button class="picker-button" type="button" @click="openAgentPicker('source')">{{ selectedSource?.name || t('engineering.productionOrder.selectSourceWarehouse') }}</button><small v-if="fieldErrors.source">{{ fieldErrors.source }}</small></label>
        <label>{{ t('engineering.productionOrder.destinationWarehouse') }} *<button class="picker-button" type="button" @click="openAgentPicker('destination')">{{ selectedDestination?.name || t('engineering.productionOrder.selectDestinationWarehouse') }}</button><small v-if="fieldErrors.destination">{{ fieldErrors.destination }}</small></label>
      </div>
    </section>

    <section>
      <h3>{{ t('engineering.simpleOrder.operations') }}</h3>
      <p v-if="bomLoading">{{ t('engineering.simpleOrder.loadingBom') }}</p>
      <p v-else-if="!selectedProduct">{{ t('engineering.simpleOrder.chooseProductForBom') }}</p>
      <p v-else-if="!activeBom?.operations.length" class="form-error">{{ t('engineering.simpleOrder.bomRequired') }}</p>
      <ol v-else class="operations">
        <li v-for="operation in activeBom.operations" :key="operation.id"><strong>{{ operation.operation_sequence }}. {{ operation.operation_name }}</strong><span>{{ operation.department_name || '—' }}</span></li>
      </ol>
        </section>
      </div>

      <section>
        <div class="section-heading"><div><h3>{{ t('engineering.simpleOrder.matrix') }}</h3><p>{{ t('engineering.simpleOrder.matrixHelp') }}</p></div></div>
        <p class="size-hint">{{ t('engineering.simpleOrder.sizeHint') }}</p>
      <div class="matrix-scroll">
        <table class="matrix-table">
          <thead>
            <tr>
              <th class="cell-index">#</th>
              <th>{{ t('engineering.simpleOrder.color') }}</th>
              <th class="cell-size">{{ t('engineering.simpleOrder.size') }}</th>
              <th class="cell-quantity">{{ t('engineering.simpleOrder.quantity') }}</th>
              <th class="cell-action"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.variants" :key="index">
              <tr>
                <td class="cell-index">{{ index + 1 }}</td>
                <td>
                  <button class="cell-button" type="button" @click="openColorPicker(index)">{{ colorLabel(row) }}</button>
                </td>
                <td>
                  <input v-model="row.size_label" class="cell-input" list="simple-order-size-ranges" :placeholder="t('engineering.simpleOrder.sizePlaceholder')" type="text">
                </td>
                <td class="cell-quantity">
                  <input v-model.number="row.planned_quantity" class="cell-input" min="1" step="1" type="number">
                </td>
                <td class="cell-action">
                  <button class="remove" type="button" :disabled="form.variants.length === 1" @click="removeVariant(index)">{{ t('engineering.simpleOrder.remove') }}</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`variant_${index}`]" class="error-row">
                <td colspan="5" class="cell-error">{{ fieldErrors[`variant_${index}`] }}</td>
              </tr>
            </template>
            <!-- Always-present extra sheet row: the only way to add a combination. -->
            <tr class="add-row">
              <td colspan="5">
                <button class="add-row-button" type="button" @click="addVariant">
                  <Plus :size="16" aria-hidden="true" />
                  {{ t('engineering.simpleOrder.addCombination') }}
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3">{{ t('engineering.simpleOrder.total') }}</td>
              <td class="cell-quantity total-value">{{ totalQuantity }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <datalist id="simple-order-size-ranges">
        <option v-for="size in sizeOptions" :key="size" :value="size" />
      </datalist>
      <small v-if="fieldErrors.total" class="form-error">{{ fieldErrors.total }}</small>
      </section>
    </div>
  </form>

  <ProductPickerModal :open="productPickerOpen" :title="t('engineering.simpleOrder.selectProduct')" product-type="manufactured_good" :product-types="['manufactured_good', 'finished_good', 'semi_finished_good', 'semi_finished']" @close="productPickerOpen = false" @select="selectProduct" />
  <ColorPickerModal :open="colorPickerOpen" :title="t('engineering.simpleOrder.selectColor')" :colors="colors" @close="colorPickerOpen = false" @select="selectColor" />
  <AgentPickerModal :open="agentPickerOpen" :title="pickerTitle" :eyebrow="agentTarget === 'department' ? t('engineering.productionOrder.browseDepartmentsTitle') : t('engineering.productionOrder.browseWarehousesTitle')" :search-label="agentTarget === 'department' ? t('engineering.productionOrder.searchDepartments') : t('engineering.productionOrder.searchWarehouses')" :search-placeholder="agentTarget === 'department' ? t('engineering.productionOrder.searchDepartmentPlaceholder') : t('engineering.productionOrder.searchWarehousePlaceholder')" :empty-message="agentTarget === 'department' ? t('engineering.productionOrder.noDepartments') : t('engineering.productionOrder.noWarehouses')" :items="pickerItems" @close="agentPickerOpen = false" @select="selectAgent" />
</template>

<style scoped>
.page-toolbar { margin-bottom: 16px; }.back-link { color: #285b9a; font-weight: 700; }.simple-order { display: grid; gap: 16px; }.simple-order > header { display: flex; align-items: center; justify-content: space-between; gap: 20px; }.eyebrow { margin: 0; color: #66726e; font-size: 11px; font-weight: 800; text-transform: uppercase; }h2 { margin: 3px 0; }header p:last-child { margin: 0; color: #66726e; }section { padding: 20px; border: 1px solid #d7dfdc; background: #fff; }h3 { margin: 0 0 14px; }.form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }input { box-sizing: border-box; width: 100%; padding: 10px; border: 1px solid #b9c5c1; font: inherit; }button { padding: 10px 15px; border: 0; color: #fff; background: #3568a8; font-weight: 700; }button:disabled { opacity: .55; }.picker-button,.secondary { border: 1px solid #b9c5c1; color: #285b9a; background: #fff; text-align: left; }.section-heading { display: flex; align-items: start; justify-content: space-between; gap: 12px; }.section-heading p { margin: 0; color: #66726e; }.operations { display: grid; gap: 8px; margin: 0; padding-left: 24px; }.operations li { padding: 9px; background: #f3f6f5; }.operations span { margin-left: 12px; color: #66726e; }.matrix-row small { grid-column: 1 / -1; color: #852d22; }.remove { border: 1px solid #d7a8a1; color: #852d22; background: #fff; }.form-error, label small { color: #852d22; }@media (max-width: 720px) { .simple-order > header,.section-heading { align-items: stretch; flex-direction: column; }.form-grid,.matrix-row { grid-template-columns: 1fr; }.matrix-action { width: 100%; } }
</style>

<style scoped>
/* Desktop: setup + BOM cards on the left, the colour/size matrix on the right. */
.order-layout { display: grid; grid-template-columns: minmax(320px, 420px) minmax(0, 1fr); gap: 16px; align-items: start; }
.order-column { display: grid; gap: 16px; }
/* Every order setup field is its own row inside the narrow setup card. */
.simple-order .form-grid { grid-template-columns: 1fr; }
.size-hint { margin: 0 0 4px; color: #66726e; font-size: 12px; }
/* Excel-style matrix: field labels are the header row, entries fill the cells. */
.matrix-scroll { margin-top: 10px; overflow-x: auto; }
.matrix-table { width: 100%; border-collapse: collapse; border: 1px solid #b9c5c1; background: #fff; }
.matrix-table th, .matrix-table td { padding: 0; border: 1px solid #d7dfdc; vertical-align: middle; }
.matrix-table thead th { padding: 9px 10px; color: #53625d; background: #eef2f1; font-size: 11px; font-weight: 800; letter-spacing: .03em; text-align: left; text-transform: uppercase; }
.matrix-table .cell-index { width: 46px; padding: 8px 6px; color: #8a958f; background: #f7fafa; font-size: 12px; text-align: center; }
.matrix-table .cell-size { width: 200px; }
.matrix-table .cell-quantity { width: 140px; }
.matrix-table .cell-action { width: 130px; }
.matrix-table .cell-input, .matrix-table .cell-button { width: 100%; min-height: 42px; box-sizing: border-box; padding: 9px 10px; border: 0; background: transparent; color: inherit; font: inherit; text-align: left; }
.matrix-table .cell-button { color: #285b9a; cursor: pointer; }
.matrix-table .cell-input:focus, .matrix-table .cell-button:focus { outline: 2px solid #3568a8; outline-offset: -2px; }
.matrix-table td.cell-quantity .cell-input { text-align: right; font-variant-numeric: tabular-nums; }
.matrix-table .cell-action .remove { margin: 6px; }
.matrix-table tbody tr:hover td { background: #f7fafa; }
.matrix-table .error-row td { padding: 6px 10px; color: #852d22; background: #fff5f3; font-size: 12px; font-weight: 700; }
.matrix-table .add-row td { padding: 0; background: #fbfcfc; }
.matrix-table .add-row-button { display: flex; align-items: center; gap: 8px; width: 100%; min-height: 42px; padding: 9px 10px; border: 0; background: transparent; color: #285b9a; font-weight: 700; text-align: left; cursor: pointer; }
.matrix-table .add-row-button:hover { background: #eef4f9; }
.matrix-table .add-row-button:focus-visible { outline: 2px solid #3568a8; outline-offset: -2px; }
.matrix-table tfoot td { padding: 9px 10px; color: #14243a; background: #eef2f1; font-weight: 800; }
.matrix-table tfoot .total-value { text-align: right; font-variant-numeric: tabular-nums; }
@media (max-width: 1000px) { .order-layout { grid-template-columns: 1fr; } }
</style>
